/**
 * Zoom Integration API
 * 
 * Frontend API utilities for Zoom integration.
 * Handles OAuth flow, recording fetching, and import operations.
 */

import { callFirebaseFunction, callHttpFunction } from '../firebaseCalls';
import { auth } from '../../firebaseClient';

/**
 * Initiate Zoom OAuth flow
 * Opens popup window for user to authorize Zoom access
 * @returns {Promise<Object>} Object with authUrl for redirect
 */
export const connectZoom = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to connect Zoom');
    }

    const token = await currentUser.getIdToken();

    // Call HTTP endpoint to get OAuth URL
    // callHttpFunction already adds Authorization header, so we pass token in options
    const functionUrl = `https://us-central1-evalpro2.cloudfunctions.net/initiate_zoom_oauth`;

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to initiate Zoom OAuth');
    }

    return result;
  } catch (error) {
    console.error('Error initiating Zoom OAuth:', error);
    throw error;
  }
};

/**
 * Get Zoom connection status for tenant (tenant-based)
 * @returns {Promise<Object>} Connection status with connected flag and zoomEmail
 */
export const getZoomConnectionStatus = async () => {
  try {
    const result = await callFirebaseFunction('getZoomConnectionStatus', {});

    const res = result as any;
    if (!res.success) {
      throw new Error(res.error || 'Failed to get Zoom connection status');
    }

    return {
      connected: res.connected || false,
      zoomEmail: res.zoomEmail || null,
      connectedAt: res.connectedAt || null,
      connectedBy: res.connectedBy || null
    };
  } catch (error: any) {
    console.error('Error getting Zoom connection status:', error);
    return { connected: false, error: (error as any).message };
  }
};

/**
 * Disconnect Zoom account
 * Revokes tokens and clears connection
 * @returns {Promise<Object>} Success result
 */
export const disconnectZoom = async () => {
  try {
    const result = await callFirebaseFunction('disconnectZoom', {});

    const res = result as any;
    if (!res.success) {
      throw new Error(res.error || 'Failed to disconnect Zoom');
    }

    return res;
  } catch (error) {
    console.error('Error disconnecting Zoom:', error);
    throw error;
  }
};

/**
 * Fetch Zoom recordings for the current user
 * @param {Object} options - Fetch options
 * @param {string} options.fromDate - Start date (YYYY-MM-DD format)
 * @param {string} options.toDate - End date (YYYY-MM-DD format)
 * @param {number} options.pageSize - Number of results per page (default 30, max 300)
 * @param {string} options.nextPageToken - Token for pagination
 * @returns {Promise<Object>} Recordings data with list and pagination info
 */
export const fetchZoomRecordings = async (options: any = {}): Promise<any> => {
  try {
    const { fromDate, toDate, pageSize = 30, nextPageToken } = options;

    const result = await callFirebaseFunction('fetchZoomRecordings', {
      fromDate,
      toDate,
      pageSize: Math.min(pageSize, 300), // Enforce max
      nextPageToken
    });

    const res = result as any;
    if (!res.success) {
      throw new Error(res.error || 'Failed to fetch recordings');
    }

    return {
      success: true,
      recordings: res.recordings || [],
      nextPageToken: res.nextPageToken || null
    };
  } catch (error) {
    console.error('Error fetching Zoom recordings:', error);
    throw error;
  }
};

/**
 * Import a Zoom recording into EvalPro2
 * Downloads recording and creates a presentation
 * @param {Object} recordingData - Recording data
 * @param {string} recordingData.recordingId - Zoom recording file ID
 * @param {string} recordingData.meetingId - Zoom meeting ID
 * @param {string} recordingData.meetingUuid - Zoom meeting UUID
 * @param {string} recordingData.downloadUrl - Zoom download URL
 * @param {string} recordingData.topic - Meeting topic/title
 * @param {Object} presentationDetails - Optional presentation details
 * @param {string} presentationDetails.description - Presentation description
 * @param {string} presentationDetails.productName - Product name
 * @param {string} presentationDetails.presentationType - Presentation type
 * @param {string} presentationDetails.consultantId - Consultant ID
 * @returns {Promise<Object>} Import result with presentationId
 */
export const importZoomRecording = async (recordingData: any, presentationDetails: any = {}): Promise<any> => {
  try {
    const {
      recordingId,
      meetingId,
      meetingUuid,
      downloadUrl,
      topic,
      startTime,
      duration,
      fileSize
    } = recordingData;

    if (!recordingId || !meetingId || !meetingUuid || !downloadUrl) {
      throw new Error('Missing required recording data');
    }

    const result = await callFirebaseFunction('importZoomRecording', {
      recordingId,
      meetingId,
      meetingUuid,
      downloadUrl,
      topic: topic || 'Zoom Recording',
      startTime,
      duration,
      fileSize,
      ...presentationDetails
    });

    const res = result as any;
    if (!res.success) {
      throw new Error(res.error || 'Failed to import recording');
    }

    return {
      success: true,
      presentationId: res.presentationId
    };
  } catch (error) {
    console.error('Error importing Zoom recording:', error);
    throw error;
  }
};

/**
 * Manually refresh Zoom access token
 * Usually called automatically, but can be triggered manually if needed
 * @returns {Promise<Object>} Refresh result
 */
export const refreshZoomToken = async () => {
  try {
    const result = await callFirebaseFunction('refreshZoomToken', {});

    const res = result as any;
    if (!res.success) {
      throw new Error(res.error || 'Failed to refresh Zoom token');
    }

    return result;
  } catch (error) {
    console.error('Error refreshing Zoom token:', error);
    throw error;
  }
};

/**
 * Get Zoom auto-import settings (tenant-level defaults + user-level overrides)
 * @returns {Promise<Object>} Auto-import settings with tenant defaults and user overrides
 */
export const getZoomAutoImportSettings = async () => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../../firebaseClient');

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    // Get user's tenant ID from token
    const tokenResult = await currentUser.getIdTokenResult();
    const tenantId = tokenResult.claims.tenantId as string;

    if (!tenantId) {
      throw new Error('User not associated with any tenant');
    }

    // Get tenant document for tenant-level defaults
    const tenantRef = doc(db, 'tenants', tenantId);
    const tenantSnap = await getDoc(tenantRef);

    let tenantDefaults = {
      enabled: false,
      defaultSchemaId: null,
      defaultFilters: {
        minDuration: 1800,
        topicKeywords: [],
        allowedDaysOfWeek: [1, 2, 3, 4, 5]
      }
    };

    if (tenantSnap.exists()) {
      const tenantData = tenantSnap.data();
      const autoImport = tenantData.zoomIntegration?.autoImport || {};
      if (typeof autoImport === 'object' && autoImport !== null) {
        tenantDefaults = {
          enabled: autoImport.enabled || false,
          defaultSchemaId: autoImport.defaultSchemaId || null,
          defaultFilters: autoImport.defaultFilters || tenantDefaults.defaultFilters
        };
      }
    }

    // Get user document for user-level filter overrides
    const userRef = doc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);

    let userOverrides = {};

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const zoomIntegration = userData.zoomIntegration || {};
      if (zoomIntegration.autoImportFilters) {
        userOverrides = zoomIntegration.autoImportFilters;
      }
    }

    // Merge: user overrides take precedence over tenant defaults
    const mergedFilters = {
      ...tenantDefaults.defaultFilters,
      ...userOverrides
    };

    return {
      autoImport: tenantDefaults.enabled,
      defaultSchemaId: tenantDefaults.defaultSchemaId,
      filters: mergedFilters,
      tenantDefaults: tenantDefaults.defaultFilters,
      userOverrides: userOverrides
    };
  } catch (error) {
    console.error('Error getting Zoom auto-import settings:', error);
    throw error;
  }
};

/**
 * Update Zoom auto-import settings (tenant-level for admins, user-level overrides for users)
 * @param {Object} settings - Auto-import settings
 * @param {boolean} settings.autoImport - Enable/disable auto-import (tenant-level, admin only)
 * @param {string} settings.defaultSchemaId - Default evaluation schema ID (tenant-level, admin only)
 * @param {Object} settings.filters - Filter configuration (user-level overrides)
 * @param {number} settings.filters.minDuration - Minimum duration in seconds
 * @param {string[]} settings.filters.topicKeywords - Topic keywords to match
 * @param {number[]} settings.filters.allowedDaysOfWeek - Allowed days (0-6, 0=Sunday)
 * @param {boolean} settings.isTenantAdmin - Whether user is tenant admin (determines update scope)
 * @returns {Promise<Object>} Update result
 */
export const updateZoomAutoImportSettings = async (settings: any): Promise<any> => {
  try {
    const { doc, updateDoc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../../firebaseClient');

    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    // Get user's tenant ID and role from token
    const tokenResult = await currentUser.getIdTokenResult();
    const tenantId = tokenResult.claims.tenantId as string;
    const isTenantAdmin = tokenResult.claims.role === 'tenantAdmin' || tokenResult.claims.isTenantAdmin;

    if (!tenantId) {
      throw new Error('User not associated with any tenant');
    }

    // If tenant admin, update tenant-level defaults
    if (isTenantAdmin && (settings.autoImport !== undefined || settings.defaultSchemaId !== undefined)) {
      const tenantRef = doc(db, 'tenants', tenantId);
      const tenantSnap = await getDoc(tenantRef);

      if (!tenantSnap.exists()) {
        throw new Error('Tenant not found');
      }

      const updateData: any = {};
      if (settings.autoImport !== undefined) {
        updateData['zoomIntegration.autoImport.enabled'] = settings.autoImport;
      }
      if (settings.defaultSchemaId !== undefined) {
        updateData['zoomIntegration.autoImport.defaultSchemaId'] = settings.defaultSchemaId;
      }
      if (settings.filters && Object.keys(settings.filters).length > 0) {
        // Tenant admins can also set default filters
        updateData['zoomIntegration.autoImport.defaultFilters'] = settings.filters;
      }

      await updateDoc(tenantRef, updateData);
    }

    // All users can update their own filter overrides
    if (settings.filters && Object.keys(settings.filters).length > 0) {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        'zoomIntegration.autoImportFilters': settings.filters
      });
    }

    return { success: true, message: 'Auto-import settings updated' };
  } catch (error) {
    console.error('Error updating Zoom auto-import settings:', error);
    throw error;
  }
};

